import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Save, Plus, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TalentProfileEdit = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [skillInput, setSkillInput] = useState('');
  
  const [formData, setFormData] = useState({
    display_name: '',
    location_suburb: '',
    location_state: 'NSW',
    willing_to_relocate: false,
    remote_preference: 'flexible',
    desired_roles: [],
    work_type_preferences: [],
    salary_expectation_min: '',
    salary_expectation_max: '',
    salary_unit: 'year',
    availability_start_date: '',
    skills: [],
    experience_summary: '',
    education_summary: '',
    certificates: []
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/talent/me`);
      const profile = response.data;
      
      // Populate form with existing data
      setFormData({
        display_name: profile.display_name || user?.full_name || '',
        location_suburb: profile.location_suburb || '',
        location_state: profile.location_state || 'NSW',
        willing_to_relocate: profile.willing_to_relocate || false,
        remote_preference: profile.remote_preference || 'flexible',
        desired_roles: profile.desired_roles || [],
        work_type_preferences: profile.work_type_preferences || [],
        salary_expectation_min: profile.salary_expectation_min || '',
        salary_expectation_max: profile.salary_expectation_max || '',
        salary_unit: profile.salary_unit || 'year',
        availability_start_date: profile.availability_start_date || '',
        skills: profile.skills || [],
        experience_summary: profile.experience_summary || '',
        education_summary: profile.education_summary || '',
        certificates: profile.certificates || []
      });
    } catch (error) {
      console.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const addSkill = () => {
    if (skillInput.trim() && !formData.skills.includes(skillInput.trim())) {
      setFormData({
        ...formData,
        skills: [...formData.skills, skillInput.trim()]
      });
      setSkillInput('');
    }
  };

  const removeSkill = (skill) => {
    setFormData({
      ...formData,
      skills: formData.skills.filter(s => s !== skill)
    });
  };

  const toggleWorkType = (type) => {
    if (formData.work_type_preferences.includes(type)) {
      setFormData({
        ...formData,
        work_type_preferences: formData.work_type_preferences.filter(t => t !== type)
      });
    } else {
      setFormData({
        ...formData,
        work_type_preferences: [...formData.work_type_preferences, type]
      });
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      await axios.post(`${API}/talent/me/profile`, formData);
      toast.success('Profile updated successfully!');
      navigate('/talent/me');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">Edit Your Profile</h1>
          <p className="text-muted-foreground">Complete your professional profile to attract employers</p>
        </div>

        <form onSubmit={handleSave} className="space-y-6">
          {/* Basic Info */}
          <Card>
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium text-foreground block mb-2">
                  Display Name <span className="text-red-500">*</span>
                </label>
                <Input
                  value={formData.display_name}
                  onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                  placeholder="First name + Last initial (e.g., Sarah M.)"
                  required
                />
                <p className="text-xs text-muted-foreground mt-1">
                  This is how employers will see your name
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-foreground block mb-2">
                    Location <span className="text-red-500">*</span>
                  </label>
                  <Input
                    value={formData.location_suburb}
                    onChange={(e) => setFormData({ ...formData, location_suburb: e.target.value })}
                    placeholder="Sydney"
                    required
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-foreground block mb-2">State</label>
                  <select
                    value={formData.location_state}
                    onChange={(e) => setFormData({ ...formData, location_state: e.target.value })}
                    className="w-full px-3 py-2 border border-border rounded-lg bg-background"
                  >
                    <option value="NSW">NSW</option>
                    <option value="VIC">VIC</option>
                    <option value="QLD">QLD</option>
                    <option value="WA">WA</option>
                    <option value="SA">SA</option>
                    <option value="TAS">TAS</option>
                    <option value="ACT">ACT</option>
                    <option value="NT">NT</option>
                  </select>
                </div>
              </div>

              <div className="space-y-3">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.willing_to_relocate}
                    onChange={(e) => setFormData({ ...formData, willing_to_relocate: e.target.checked })}
                    className="w-5 h-5"
                  />
                  <span className="text-sm font-medium text-foreground">Willing to relocate</span>
                </label>

                <div>
                  <label className="text-sm font-medium text-foreground block mb-2">Remote Work Preference</label>
                  <select
                    value={formData.remote_preference}
                    onChange={(e) => setFormData({ ...formData, remote_preference: e.target.value })}
                    className="w-full px-3 py-2 border border-border rounded-lg bg-background"
                  >
                    <option value="on_site">On-site only</option>
                    <option value="hybrid">Hybrid preferred</option>
                    <option value="remote">Remote only</option>
                    <option value="flexible">Flexible</option>
                  </select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Work Preferences */}
          <Card>
            <CardHeader>
              <CardTitle>Work Preferences</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium text-foreground block mb-2">
                  Work Type Preferences
                </label>
                <div className="flex flex-wrap gap-2">
                  {['full_time', 'part_time', 'contract', 'casual'].map((type) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => toggleWorkType(type)}
                      className={`px-4 py-2 rounded-lg border transition-colors ${
                        formData.work_type_preferences.includes(type)
                          ? 'bg-green-600 text-white border-green-600'
                          : 'bg-background border-border hover:bg-muted'
                      }`}
                    >
                      {type.replace('_', '-')}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-foreground block mb-2">
                  Salary Expectations (Optional)
                </label>
                <div className="grid md:grid-cols-3 gap-4">
                  <Input
                    type="number"
                    value={formData.salary_expectation_min}
                    onChange={(e) => setFormData({ ...formData, salary_expectation_min: e.target.value })}
                    placeholder="Min"
                  />
                  <Input
                    type="number"
                    value={formData.salary_expectation_max}
                    onChange={(e) => setFormData({ ...formData, salary_expectation_max: e.target.value })}
                    placeholder="Max"
                  />
                  <select
                    value={formData.salary_unit}
                    onChange={(e) => setFormData({ ...formData, salary_unit: e.target.value })}
                    className="px-3 py-2 border border-border rounded-lg bg-background"
                  >
                    <option value="year">Per Year</option>
                    <option value="hour">Per Hour</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-foreground block mb-2">
                  Availability Start Date
                </label>
                <Input
                  type="date"
                  value={formData.availability_start_date}
                  onChange={(e) => setFormData({ ...formData, availability_start_date: e.target.value })}
                />
              </div>
            </CardContent>
          </Card>

          {/* Skills */}
          <Card>
            <CardHeader>
              <CardTitle>Skills</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium text-foreground block mb-2">
                  Add Skills
                </label>
                <div className="flex gap-2">
                  <Input
                    value={skillInput}
                    onChange={(e) => setSkillInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSkill())}
                    placeholder="e.g., Python, React, Project Management"
                  />
                  <button
                    type="button"
                    onClick={addSkill}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    <Plus className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                {formData.skills.map((skill) => (
                  <Badge 
                    key={skill}
                    className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 flex items-center gap-2 px-3 py-1"
                  >
                    {skill}
                    <button
                      type="button"
                      onClick={() => removeSkill(skill)}
                      className="hover:text-red-600"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Experience */}
          <Card>
            <CardHeader>
              <CardTitle>Experience & Education</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium text-foreground block mb-2">
                  Experience Summary
                </label>
                <Textarea
                  value={formData.experience_summary}
                  onChange={(e) => setFormData({ ...formData, experience_summary: e.target.value })}
                  placeholder="Briefly describe your work experience and achievements..."
                  rows={5}
                />
              </div>

              <div>
                <label className="text-sm font-medium text-foreground block mb-2">
                  Education Summary
                </label>
                <Textarea
                  value={formData.education_summary}
                  onChange={(e) => setFormData({ ...formData, education_summary: e.target.value })}
                  placeholder="Your education background and qualifications..."
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>

          {/* Save Buttons */}
          <div className="flex gap-4">
            <button
              type="button"
              onClick={() => navigate('/talent/me')}
              className="flex-1 px-6 py-3 border border-border rounded-lg hover:bg-muted transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
            >
              <Save className="w-5 h-5" />
              {saving ? 'Saving...' : 'Save Profile'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TalentProfileEdit;
